import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  ThumbUp,
  ThumbUpOutlined,
  Send,
} from '@mui/icons-material';
import { NewsItemDto, NewsCommentDto, NewsClient } from '../../../app/services/api/news';
import { getDefaultNewsImage } from '../../../assets';

interface NewsModalProps {
  open: boolean;
  item: NewsItemDto | null;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onItemUpdated?: (newsItem: NewsItemDto) => void;
}

function formatFullDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCommentDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function NewsModal({ open, item, onClose, onItemUpdated }: NewsModalProps) {
  const theme = useTheme();
  const [comments, setComments] = useState<NewsCommentDto[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [liked, setLiked] = useState(false);
  const [currentItem, setCurrentItem] = useState<NewsItemDto | null>(item);
  const [imageError, setImageError] = useState(false);

  const newsClient = new NewsClient();

  // Check if user has already liked this item (persisted in localStorage)
  const getLikedState = (newsItemId: number): boolean => {
    const likedItems = JSON.parse(localStorage.getItem('likedNewsItems') || '[]');
    return likedItems.includes(newsItemId);
  };

  const setLikedState = (newsItemId: number, isLiked: boolean) => {
    const likedItems = JSON.parse(localStorage.getItem('likedNewsItems') || '[]');
    if (isLiked && !likedItems.includes(newsItemId)) {
      likedItems.push(newsItemId);
    } else if (!isLiked) {
      const index = likedItems.indexOf(newsItemId);
      if (index > -1) likedItems.splice(index, 1);
    }
    localStorage.setItem('likedNewsItems', JSON.stringify(likedItems));
  };

  useEffect(() => {
    setCurrentItem(item);
    setImageError(false);
    // Load the user's like state from localStorage
    if (item) {
      setLiked(getLikedState(item.id));
    } else {
      setLiked(false);
    }
  }, [item]);

  useEffect(() => {
    if (open && item) {
      // Record view
      newsClient.recordView(item.id);

      // Load comments
      setLoadingComments(true);
      newsClient.getComments(item.id)
        .then(setComments)
        .catch(() => setComments([]))
        .finally(() => setLoadingComments(false));
    }
  }, [open, item?.id]);

  const handleLike = async () => {
    if (!currentItem) return;

    const newLiked = !liked;
    const newLikeCount = newLiked
      ? currentItem.likeCount + 1
      : Math.max(0, currentItem.likeCount - 1);

    // Optimistic update
    setLiked(newLiked);
    setLikedState(currentItem.id, newLiked);
    const optimisticItem = { ...currentItem, likeCount: newLikeCount };
    setCurrentItem(optimisticItem);

    try {
      const updated = await newsClient.toggleLike(currentItem.id, newLiked);
      setCurrentItem(updated);
      onItemUpdated?.(updated);
    } catch (error) {
      // Revert on error
      setLiked(!newLiked);
      setLikedState(currentItem.id, !newLiked);
      setCurrentItem(currentItem);
      console.error('Failed to toggle like:', error);
    }
  };

  const handleAddComment = async () => {
    if (!currentItem || !newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const comment = await newsClient.addComment(currentItem.id, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment('');

      // Update the item's comment count
      const updatedItem = { ...currentItem, commentCount: currentItem.commentCount + 1 };
      setCurrentItem(updatedItem);
      onItemUpdated?.(updatedItem);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  if (!currentItem) return null;

  const showImageFallback = !currentItem.imageUrl || imageError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          maxHeight: '90vh',
          position: 'relative',
        },
      }}
    >
      {/* Close button - top right of modal */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: 'rgba(255,255,255,0.9)',
          '&:hover': { bgcolor: 'white' },
        }}
      >
        <Close />
      </IconButton>

      <DialogTitle sx={{ p: 0 }}>
        {/* Header image/gradient - max 33% of modal height */}
        <Box
          sx={{
            maxHeight: '33vh',
            width: '100%',
            height: 200,
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={showImageFallback
              ? getDefaultNewsImage(Math.abs(currentItem.id) % 4)
              : currentItem.imageUrl!
            }
            alt={currentItem.title}
            onError={() => setImageError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 8 }}>
          {/* Title and Tags - opposite sides */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
          >
            <Typography
              variant="textLG"
              fontWeight={700}
              sx={{ fontSize: 24, flex: 1 }}
            >
              {currentItem.title}
            </Typography>
            {currentItem.tags && currentItem.tags.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexShrink: 0, ml: 2 }}
              >
                {currentItem.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {/* Meta info */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              By {currentItem.createdByUserName}
            </Typography>
            <Typography
              variant="textSM"
              color="text.secondary"
            >
              {formatFullDate(currentItem.publishedAtUtc || currentItem.createdAtUtc)}
            </Typography>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Content */}
          <Box
            sx={{
              '& p': { mb: 2 },
              '& ul, & ol': { pl: 3, mb: 2 },
              '& li': { mb: 0.5 },
            }}
            dangerouslySetInnerHTML={{ __html: currentItem.content }}
          />

          <Divider sx={{ my: 3 }} />

          {/* Comments section */}
          <Typography
            variant="textMD"
            fontWeight={600}
            mb={2}
          >
            Comments ({comments.length})
          </Typography>

          {loadingComments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ py: 2 }}
            >
              No comments yet. Be the first to comment!
            </Typography>
          ) : (
            <Stack
              spacing={2}
              sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}
            >
              {comments.map((comment) => (
                <Stack
                  key={comment.id}
                  direction="row"
                  spacing={1.5}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {comment.createdByUserName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="baseline"
                    >
                      <Typography
                        variant="textSM"
                        fontWeight={600}
                      >
                        {comment.createdByUserName}
                      </Typography>
                      <Typography
                        variant="textSM"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        {formatCommentDate(comment.createdAtUtc)}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="textSM"
                      sx={{ mt: 0.5 }}
                    >
                      {comment.content}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}

          {/* Add comment input with like button */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
          >
            <Button
              variant={liked ? 'primary' : 'secondary'}
              size="md"
              startIcon={liked ? <ThumbUp /> : <ThumbUpOutlined />}
              onClick={handleLike}
              sx={{ flexShrink: 0 }}
            >
              {liked ? 'Liked' : 'Like'} ({currentItem.likeCount})
            </Button>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              size="small"
              disabled={submittingComment}
            />
            <IconButton
              onClick={handleAddComment}
              disabled={!newComment.trim() || submittingComment}
              color="primary"
            >
              {submittingComment ? <CircularProgress size={20} /> : <Send />}
            </IconButton>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
